import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, Check, X, Eye, ChevronDown, ChevronUp, Search, Filter, Users, CreditCard, Loader2, Edit, Download } from 'lucide-react';
import { resolveFileUrl } from '../../utils/fileUrl';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import PaginationBar from '../../components/PaginationBar';
import { formatDisplayDate } from '../../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ParichaySangamManagement = () => {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReg, setSelectedReg] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 20;

  useEffect(() => { fetchData(); }, [statusFilter, page]);

  const getToken = () => localStorage.getItem('token');

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (statusFilter) params.status = statusFilter;

      const [regRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/parichay-sangam`, { headers, params }),
        axios.get(`${API_URL}/api/parichay-sangam/stats`, { headers })
      ]);
      setRegistrations(regRes.data.registrations);
      setTotal(regRes.data.total);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/parichay-sangam/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const filtered = registrations.filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return r.candidates?.some(c => c.name?.toLowerCase().includes(s)) ||
      r.familyCode?.toLowerCase().includes(s);
  });

  const exportToCSV = () => {
    if (registrations.length === 0) { toast.error('No data to export'); return; }
    const rows = [];
    registrations.forEach(reg => {
      (reg.candidates || []).forEach((c, idx) => {
        const attendeeFields = {};
        (reg.attendees || []).forEach((a, ai) => {
          attendeeFields[`Attendee ${ai+1} Name`] = a.name || '';
          attendeeFields[`Attendee ${ai+1} Relation`] = a.relation || '';
          attendeeFields[`Attendee ${ai+1} Phone`] = a.phone || '';
        });
        rows.push({
          'Family Code': reg.familyCode || '', 'Status': reg.status || '',
          'Candidate #': idx + 1, 'Candidate ID': c.candidateId || '',
          'Name': c.name || '', 'Father Name': c.fatherName || '', 'Mother Name': c.motherName || '',
          'Nukh': c.nukh || '', 'Gender': c.gender || '',
          'Marital Status': c.maritalStatus || '', 'DOB': formatDisplayDate(c.dateOfBirth),
          'Birth Time': c.birthTime || '', 'Birth Place': c.birthPlace || '',
          'Height': c.height || '', 'Weight': c.weight || '', 'Complexion': c.complexion || '',
          'Blood Group': c.bloodGroup || '', 'Manglik': c.mapiOrManglik || '',
          'Unmarried Brothers': c.unmarriedBrothers || '', 'Married Brothers': c.marriedBrothers || '',
          'Unmarried Sisters': c.unmarriedSisters || '', 'Married Sisters': c.marriedSisters || '',
          'Maternal Name': c.maternalName || '', 'Nukh of Maternals': c.nukhOfMaternals || '',
          'Place of Maternals': c.placeOfMaternals || '',
          'Education': c.education || '', 'Occupation': c.occupation || '',
          'Working At': c.workingAt || '', 'Annual Income': c.annualIncome || '',
          'Phone': c.phone || '', 'Email': c.email || '', 'WhatsApp': c.whatsapp || '',
          'Current Address': c.currentAddress || '', 'Current City': c.currentCity || '',
          'Current State': c.currentState || '', 'Current Country': c.currentCountry || '',
          'Native Place': c.nativePlace || '', 'Native City': c.nativeCity || '',
          'Native State': c.nativeState || '',
          'Hobbies': c.hobbies || '', 'About': c.aboutCandidate || '',
          'Partner Preference': c.partnerPreference || '',
          'Photo URL': c.photoUrl || '', 'ID Card URL': c.idCardUrl || '',
          'Attending In Person': reg.attendingInPerson ? 'Yes' : 'No',
          ...attendeeFields,
          'Total Fee': reg.totalFee || '', 'Payment Amount': reg.paymentAmount || '',
          'Payment Mode': reg.paymentMode || '', 'Payment Date': reg.paymentDate || '',
          'Transaction Number': reg.transactionNumber || '',
          'Registration Date': formatDisplayDate(reg.registrationDate)
        });
      });
    });
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csvContent = [headers.join(','), ...rows.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `parichay-sangam-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('CSV exported successfully');
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  return (
    <div className="space-y-6" data-testid="parichay-management">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Heart className="w-6 h-6 text-primary" /> Parichay Sangam 2026
        </h1>
        <Button onClick={exportToCSV} variant="outline" size="sm" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[
          { label: 'Total', value: stats.total || 0, color: 'bg-blue-50 text-blue-800' },
          { label: 'Pending', value: stats.pending || 0, color: 'bg-yellow-50 text-yellow-800' },
          { label: 'Approved', value: stats.approved || 0, color: 'bg-green-50 text-green-800' },
          { label: 'Rejected', value: stats.rejected || 0, color: 'bg-red-50 text-red-800' },
          { label: 'Attendees', value: stats.totalApprovedAttendees || 0, color: 'bg-purple-50 text-purple-800' },
          { label: 'Fee Collected', value: `₹${stats.totalApprovedFee || 0}`, color: 'bg-emerald-50 text-emerald-800' },
        ].map((s, i) => (
          <Card key={i} className={`p-4 ${s.color}`}>
            <p className="text-xs font-medium opacity-70">{s.label}</p>
            <p className="text-2xl font-bold">{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name or family code..." className="pl-10" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="h-10 px-3 rounded-md border border-input bg-background text-sm">
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-semibold">Family Code</th>
                <th className="text-left p-3 font-semibold">Candidates</th>
                <th className="text-left p-3 font-semibold">Attending</th>
                <th className="text-left p-3 font-semibold">Fee</th>
                <th className="text-left p-3 font-semibold">Payment</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((reg) => (
                <tr key={reg.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-mono text-xs">{reg.familyCode}</td>
                  <td className="p-3">
                    {reg.candidates?.map((c, i) => (
                      <div key={i} className="text-sm">{c.name} <span className="text-xs text-gray-400">({c.candidateId})</span></div>
                    ))}
                  </td>
                  <td className="p-3">{reg.totalAttendingMembers || 0}</td>
                  <td className="p-3">₹{reg.totalFee || 0}</td>
                  <td className="p-3 text-xs">
                    <div>{reg.paymentMode || '-'}</div>
                    {reg.transactionNumber && <div className="text-gray-400">{reg.transactionNumber}</div>}
                  </td>
                  <td className="p-3"><StatusBadge status={reg.status} /></td>
                  <td className="p-3 text-xs text-gray-500">{formatDisplayDate(reg.registrationDate)}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setSelectedReg(reg)} className="p-2 hover:bg-gray-100 rounded" title="View Details">
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      {reg.status !== 'approved' && (
                        <button onClick={() => updateStatus(reg.id, 'approved')} className="p-2 hover:bg-green-100 rounded" title="Approve">
                          <Check className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                      {reg.status !== 'rejected' && (
                        <button onClick={() => updateStatus(reg.id, 'rejected')} className="p-2 hover:bg-red-100 rounded" title="Reject">
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center py-8 text-gray-400">No registrations found</p>}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <PaginationBar
          currentPage={page + 1}
          totalPages={Math.ceil(total / LIMIT)}
          onPageChange={(p) => setPage(p - 1)}
          totalItems={total}
          pageSize={LIMIT}
          itemLabel="registrations"
          testIdPrefix="ps-mgmt"
        />
      )}

      {/* Detail Modal */}
      {selectedReg && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedReg(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <h2 className="font-bold text-lg">Registration Details — {selectedReg.familyCode}</h2>
              <div className="flex items-center gap-2">
                <StatusBadge status={selectedReg.status} />
                <button onClick={() => setSelectedReg(null)} className="p-2 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {selectedReg.candidates?.map((c, i) => (
                <div key={i} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-primary">Candidate {i + 1}: {c.name} <span className="text-xs text-gray-400 font-mono">{c.candidateId}</span></h3>
                    {c.photoUrl && (
                      <a href={resolveFileUrl(c.photoUrl)} target="_blank" rel="noopener noreferrer">
                        <img src={resolveFileUrl(c.photoUrl)} alt={c.name} className="w-16 h-16 object-cover rounded-lg border" />
                      </a>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    {[
                      ['Father', c.fatherName], ['Mother', c.motherName], ['Nukh', c.nukh],
                      ['Gender', c.gender], ['Marital Status', c.maritalStatus],
                      ['DOB', formatDisplayDate(c.dateOfBirth)], ['Birth Time', c.birthTime], ['Birth Place', c.birthPlace],
                      ['Height', c.height], ['Weight', c.weight], ['Complexion', c.complexion],
                      ['Blood Group', c.bloodGroup], ['Manglik', c.mapiOrManglik],
                      ['Unmarried Brothers', c.unmarriedBrothers], ['Married Brothers', c.marriedBrothers],
                      ['Unmarried Sisters', c.unmarriedSisters], ['Married Sisters', c.marriedSisters],
                      ['Maternal Name', c.maternalName], ['Nukh of Maternals', c.nukhOfMaternals],
                      ['Place of Maternals', c.placeOfMaternals],
                      ['Education', c.education], ['Occupation', c.occupation],
                      ['Working At', c.workingAt], ['Annual Income', c.annualIncome],
                      ['Phone', c.phone], ['Email', c.email], ['WhatsApp', c.whatsapp],
                      ['Current City', c.currentCity], ['Current State', c.currentState],
                      ['Current Address', c.currentAddress], ['Country', c.currentCountry],
                      ['Native Place', c.nativePlace], ['Native City', c.nativeCity],
                      ['Native State', c.nativeState],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label}>
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="font-medium">{value}</p>
                      </div>
                    ))}
                  </div>
                  {c.hobbies && <div className="mt-2"><p className="text-xs text-gray-400">Hobbies</p><p className="text-sm">{c.hobbies}</p></div>}
                  {c.aboutCandidate && <div className="mt-2"><p className="text-xs text-gray-400">About</p><p className="text-sm">{c.aboutCandidate}</p></div>}
                  {c.partnerPreference && <div className="mt-2"><p className="text-xs text-gray-400">Partner Preference</p><p className="text-sm">{c.partnerPreference}</p></div>}
                  {c.idCardUrl && (
                    <div className="mt-3 pt-2 border-t">
                      <a href={`${API_URL}${c.idCardUrl}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                        View ID Card
                      </a>
                    </div>
                  )}
                </div>
              ))}

              {selectedReg.attendingInPerson && (
                <div className="border rounded-lg p-4">
                  <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Attendees</h3>
                  {selectedReg.attendees?.filter(a => a.name).map((a, i) => (
                    <div key={i} className="flex items-center gap-4 text-sm py-2 border-b last:border-b-0">
                      {a.photoUrl && (
                        <a href={resolveFileUrl(a.photoUrl)} target="_blank" rel="noopener noreferrer">
                          <img src={resolveFileUrl(a.photoUrl)} alt={a.name} className="w-12 h-12 object-cover rounded-lg border" />
                        </a>
                      )}
                      <span className="font-medium">{a.name}</span>
                      <span className="text-gray-500">{a.relation}</span>
                      <span className="text-gray-500">{a.phone}</span>
                    </div>
                  ))}
                  <div className="mt-3 pt-3 border-t flex gap-6 text-sm">
                    <span>Total Attending: <strong>{selectedReg.totalAttendingMembers}</strong></span>
                    <span>Total Fee: <strong>₹{selectedReg.totalFee}</strong></span>
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4">
                <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Payment</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-xs text-gray-400">Mode</p><p className="font-medium">{selectedReg.paymentMode || '-'}</p></div>
                  <div><p className="text-xs text-gray-400">Amount</p><p className="font-medium">{selectedReg.paymentAmount ? `₹${selectedReg.paymentAmount}` : '-'}</p></div>
                  <div><p className="text-xs text-gray-400">Date</p><p className="font-medium">{selectedReg.paymentDate || '-'}</p></div>
                  <div><p className="text-xs text-gray-400">Transaction ID</p><p className="font-medium">{selectedReg.transactionNumber || '-'}</p></div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                {selectedReg.status !== 'approved' && (
                  <Button onClick={() => { updateStatus(selectedReg.id, 'approved'); setSelectedReg(null); }} className="bg-green-600 hover:bg-green-700">Approve</Button>
                )}
                {selectedReg.status !== 'rejected' && (
                  <Button variant="destructive" onClick={() => { updateStatus(selectedReg.id, 'rejected'); setSelectedReg(null); }}>Reject</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParichaySangamManagement;
